import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { TableContentHeader, TableContentProperty } from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import { TableContentItem } from '@/corePagesModule/BaseTableContent/interface/tableContentItems';
import { NarrowState } from '@/corePagesModule/BaseTableContent/interface/uiState';
import { defineStore } from 'pinia';
import { reactive, toRaw } from 'vue';
import { IOrderControl } from './entities/IOrderControl';
import { OrderStatusFilterItemForOrderBusiness } from '@/coreInfrastructureModule/axios/generated/orderManage';
import { HEADER_MODIFY_TABLE_CONTENT_PROPERTY } from '@/corePagesModule/BaseTableContent/constants';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { simpleCopy } from '@/utils/deepCopy';
import { diContainer } from '@/coreContainer/container';
import { useTableFilter } from '@/corePagesModule/composables/useTableFilter';
import { loadPreferences, myPreferences, savePreferences } from '@/appLogic/view/preferences';
import { ORDER_MANAGE_TABLE_TYPE } from '@/orderTableModule/constants';
/**
 * useOrderControlStoreを使用して、発注管理に関連する状態とロジックを提供する。
 * @returns {object} 発注管理ストアの状態とメソッドを含むオブジェクト。
 */
export const useOrderControlStore = defineStore('orderControl', () => {
  /**
   * 発注管理ストアの状態を定義する。
   */

  // フィルター情報のComposable
  const { cachedFilterItems, bulkUpdateFilterInfos, getFilterItemsValue, initTableFilter } = useTableFilter('orderManageTable');
  const state = reactive<IOrderControlState>({
    items: [],
    onUpdatedBusiness: [] as unknown as IOrderControlState['onUpdatedBusiness'],
    // ↓↓↓↓新規追加分↓↓↓↓
    pagingOptions: initPagingOptions(30),
    sortKey: 'createdOrderDate',
    filterState: [],
    isAsc: true,
    loadingState: {
      status: {
        type: 'loading',
      },
    },
    tableContentItems: [],
    tableContentProperties: {} as TableContentHeader,
    checkedIds: [],
    orderStatusFilterItems: {
      Recommended: true,
      ApprovalRequested: true,
      ApprovedAwaitingDeliveryDate: true,
    },
    updateOrderLoadingState: {
      status: {
        type: 'success',
      },
    },
    aggregateBusinessIdsHasRecommendOrder: [],
    summaryCount: {
      Recommended: undefined,
      ApprovalRequested: undefined,
      ApprovedAwaitingDeliveryDate: undefined,
    },
    isHiddenRecommendShow: false,
  });

  /**
   * stateの初期化
   */
  const $reset = () => {
    (state.tableContentItems = []), (state.tableContentProperties = {} as TableContentHeader);
    state.loadingState = {
      status: {
        type: 'loading',
      },
    };
    state.pagingOptions = initPagingOptions(30);
    state.sortKey = 'createdOrderDate';
    state.filterState = [];
    state.isAsc = true;
    state.checkedIds = [];
    state.orderStatusFilterItems = {
      Recommended: true,
      ApprovalRequested: true,
      ApprovedAwaitingDeliveryDate: true,
    };
    state.tableContentProperties = fetchTableContentProperties();
    state.summaryCount = {
      Recommended: undefined,
      ApprovalRequested: undefined,
      ApprovedAwaitingDeliveryDate: undefined,
    };
    state.isHiddenRecommendShow = false;
  };

  /**
   * 有効な発注ステータスを取得する。
   * @returns 有効な発注ステータスの配列。
   */
  const getEnabledOrderStatus = () => {
    const enabledOrderStatus: OrderStatus[] = [];
    if (state.orderStatusFilterItems.Recommended) enabledOrderStatus.push(OrderStatus.Recommended);
    if (state.orderStatusFilterItems.ApprovalRequested) enabledOrderStatus.push(OrderStatus.ApprovalRequested);
    if (state.orderStatusFilterItems.ApprovedAwaitingDeliveryDate) enabledOrderStatus.push(OrderStatus.ApprovedAwaitingDeliveryDate);
    return enabledOrderStatus;
  };

  /**
   * 取引アップデート時に実行する関数を呼び出す。
   * @returns 非同期処理を含むプロミス。
   */
  const onNextUpdateBusiness = async () => {
    for (const onUpdatedBusiness of state.onUpdatedBusiness) {
      await onUpdatedBusiness();
    }
  };

  /**
   * ページング変更時の処理
   * TODO: これ、共通化するの？
   * @param pagingOptions - ページングオプション
   */
  const onChangePaging = async (pagingOptions: IPagingOptions) => {
    state.pagingOptions = {
      pageSize: pagingOptions.pageSize,
      offset: pagingOptions.offset,
      totalResultsAvailable: pagingOptions.totalResultsAvailable,
      returnCount: pagingOptions.returnCount,
    };
    // ページサイズ設定の永続化対応：IndexedDBに保存
    await loadPreferences();
    if (!myPreferences[ORDER_MANAGE_TABLE_TYPE]) {
      myPreferences[ORDER_MANAGE_TABLE_TYPE] = {};
    }
    myPreferences[ORDER_MANAGE_TABLE_TYPE].pageSize = state.pagingOptions.pageSize;
    await savePreferences();
    await fetchItems();
  };

  /**
   * PageSize情報はそのままに、その他のページング情報をリセットする
   */
  const resetPagingOptions = () => {
    // pagingOptionsがnullableなので、取れなかった場合は30で初期化する
    state.pagingOptions = initPagingOptions(state.pagingOptions ? state.pagingOptions.pageSize : 30);
  };

  /**
   * 発注管理情報の取得
   */
  const fetchItems = async () => {
    state.loadingState = {
      status: {
        type: 'loading',
      },
    };

    await initTableFilter();

    // 永続化された設定を読み込む
    await loadPreferences();
    const savedPreferences = myPreferences[ORDER_MANAGE_TABLE_TYPE];

    if (savedPreferences) {
      // ステータスフィルターの適用
      if (savedPreferences.orderStatusFilter && Object.keys(savedPreferences.orderStatusFilter).length > 0) {
        state.orderStatusFilterItems = savedPreferences.orderStatusFilter;
      }

      // ソート設定の適用
      if (savedPreferences.sortKey) {
        state.sortKey = savedPreferences.sortKey;
      }
      if (savedPreferences.isAsc !== undefined) {
        state.isAsc = savedPreferences.isAsc;
      }

      // ページサイズ設定の適用
      if (savedPreferences.pageSize) {
        if (state.pagingOptions) {
          state.pagingOptions.pageSize = savedPreferences.pageSize;
        } else {
          state.pagingOptions = { ...initPagingOptions(savedPreferences.pageSize), pageSize: savedPreferences.pageSize };
        }
      }
    }

    // UIのソート表示を更新
    if (state.sortKey) {
      state.tableContentProperties.contentProperties = state.tableContentProperties.contentProperties.map((property) => {
        // state.isAscがundefinedの場合に備えてデフォルト値（true）を設定
        const currentIsAsc = state.isAsc ?? true;
        // ソート対象のキーと一致する場合はstate.isAscを適用し、それ以外は既存のソート方向を維持
        property.sortOption.isAsc = property.key === state.sortKey ? currentIsAsc : property.sortOption.isAsc;
        return property;
      });
    }

    const result = await diContainer.getOrderControlUseCase.Execute(
      state.pagingOptions,
      state.sortKey,
      state.isAsc,
      cachedFilterItems.value,
      { ...state.orderStatusFilterItems, HiddenRecommend: state.isHiddenRecommendShow }
    );
    switch (result.status.type) {
      case 'success':
        state.items = result.status.data.result;
        state.pagingOptions = result.status.data.pagingOptions;
        state.summaryCount = result.status.data.summaryCount;
        state.tableContentItems = convertOrderControlToTableContentItems(state.items);

        if ((result.status.data.pagingOptions?.returnCount ?? 0) === 0) {
          state.loadingState.status.type = 'zeroMatch';
        } else {
          state.loadingState.status.type = 'success';
        }
        break;
      case 'error':
        state.loadingState = {
          status: {
            type: 'error',
            errorMessage: '取得に失敗しました', // TODO: 本来はresult.status.error.message
          },
        };
        break;
    }
  };

  /**
   * 発注管理情報をテーブル表示用の情報に変換する
   * @param orderControls - 発注管理情報の配列
   * @returns テーブル表示用の情報
   */
  const convertOrderControlToTableContentItems = (orderControls: IOrderControl[]): TableContentItem[] => {
    const displayTableContent: TableContentItem[] = [];
    orderControls.forEach((orderControl, index) => {
      displayTableContent.push({
        displayId: `${orderControl.aggregateBusinessId}-${index}`,
        isVirtualChild: false, // TODO: とりあえずね
        ...orderControl,
      });
    });
    return displayTableContent;
  };

  /**
   * テーブルのデータをソートする
   * @param sortKey - ソートキー
   * @param isAsc - 昇順かどうか
   */
  const sortTableContentItems = async (sortKey: TableContentProperty, isAsc: boolean) => {
    let modifiedSortKey = sortKey.key.slice('OrderControl'.length).replace(/^./, (c) => c.toLowerCase());
    if (modifiedSortKey === 'orderDate') {
      modifiedSortKey = 'createdOrderDate';
    } else if (modifiedSortKey === 'deliveryDate') {
      modifiedSortKey = 'desiredDeliveryDate';
    } else if (modifiedSortKey === 'aggregateBusinessResponsibleUsers') {
      modifiedSortKey = 'aggregateBusinessResponsibleUser';
    }
    state.sortKey = modifiedSortKey;
    state.isAsc = isAsc;
    // ソート設定の永続化対応：IndexedDBに保存
    await loadPreferences();
    if (!myPreferences[ORDER_MANAGE_TABLE_TYPE]) {
      myPreferences[ORDER_MANAGE_TABLE_TYPE] = {};
    }
    myPreferences[ORDER_MANAGE_TABLE_TYPE].sortKey = state.sortKey;
    myPreferences[ORDER_MANAGE_TABLE_TYPE].isAsc = state.isAsc;
    await savePreferences();
    state.tableContentProperties.contentProperties = state.tableContentProperties.contentProperties.map((property) => {
      property.sortOption.isAsc = property.key === sortKey.key ? isAsc : true;
      return property;
    });
    if (state.pagingOptions) state.pagingOptions.offset = 0;
    fetchItems();
  };

  /**
   * テーブルのデータをフィルターする
   * @param narrowState - 絞り込み状態
   */
  const filterTableContentItems = (narrowState: NarrowState[]) => {
    state.filterState = narrowState;
    fetchItems();
  };

  /**
   * フィルターモーダル適用ボタンクリック時の処理
   * @param filterItems - フィルターモーダル内で選択された項目
   */
  const onAppliedFilterModal = async (filterItems: FilterContentItem[]) => {
    await bulkUpdateFilterInfos(simpleCopy(filterItems));
    // フィルター時はページング情報をリセットして取得する
    resetPagingOptions();
    await fetchItems();
  };

  /**
   * 発注ステータスフィルターの項目を変更する
   * @param orderStatus - 変更する発注ステータス
   * @returns 非同期処理を含むプロミス
   */
  const changeOrderStatusFilterItems = async (orderStatus: OrderStatus) => {
    switch (orderStatus) {
      case OrderStatus.Recommended:
        state.orderStatusFilterItems.Recommended = !state.orderStatusFilterItems.Recommended;
        break;
      case OrderStatus.ApprovalRequested:
        state.orderStatusFilterItems.ApprovalRequested = !state.orderStatusFilterItems.ApprovalRequested;
        break;
      case OrderStatus.ApprovedAwaitingDeliveryDate:
        state.orderStatusFilterItems.ApprovedAwaitingDeliveryDate = !state.orderStatusFilterItems.ApprovedAwaitingDeliveryDate;
        break;
    }
    if (state.pagingOptions) state.pagingOptions.offset = 0;
    // ステータスフィルターの永続化対応：IndexedDBに保存
    await loadPreferences();
    if (!myPreferences[ORDER_MANAGE_TABLE_TYPE]) {
      myPreferences[ORDER_MANAGE_TABLE_TYPE] = {};
    }
    myPreferences[ORDER_MANAGE_TABLE_TYPE].orderStatusFilter = toRaw(state.orderStatusFilterItems);
    await savePreferences();
    await fetchItems();
  };

  /**
   * 推奨情報を含むかどうかをチェックする
   * @param checkedIds - チェックされたアイテムのIDの配列
   * @returns 推奨情報を含む場合はtrue、含まない場合はfalse
   */
  const containsRecommendOrder = (checkedIds: string[]) => {
    const orderIds: string[] = state.tableContentItems.filter((item) => checkedIds.includes(item.displayId)).map((item) => item.orderId);
    return orderIds.includes('');
  };

  /**
   * 選択したdisplayIdに紐づく集計の推奨の表示非表示を保存する
   * @param checkedIds - 指定するdisplayId
   * @param isHidden - 非表示にするかどうか
   */
  const putHiddenRecommended = async (checkedIds: string[], isHidden: boolean) => {
    const aggregateBusinessIds: string[] = state.tableContentItems
      .filter((item) => checkedIds.includes(item.displayId))
      .map((item) => item.aggregateBusinessId);

    const res = await diContainer.putRecommendHiddenUseCase.execute(aggregateBusinessIds, isHidden);
    if (res.status.type === 'success') {
      return true;
    } else {
      return false;
    }
  };

  /**
   * テーブルのヘッダー情報を取得
   */
  const fetchTableContentProperties = (): TableContentHeader => {
    return {
      contentProperties: [
        {
          key: 'OrderControlChoice',
          isVariableWidth: false,
          isNarrowable: false,
          isEditable: false,
          sortOption: { isSortable: false, isAsc: true },
          propertyPath: { paths: ['choice'], propertyType: 'string' },
        },
        {
          key: 'OrderConfirmation',
          isVariableWidth: false,
          isNarrowable: false,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: false,
          },
          propertyPath: { paths: ['orderStatus'], propertyType: 'string' },
        },
        {
          key: 'OrderControlOrderStatus',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['orderStatus'], propertyType: 'string' },
        },
        {
          key: 'OrderControlLastCheckedAt',
          isVariableWidth: true,
          isNarrowable: false,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: true,
          },
          propertyPath: { paths: ['lastCheckedAt'], propertyType: 'string' },
        },
        {
          key: 'OrderControlOrderDate',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['orderDate'], propertyType: 'string' },
        },
        {
          key: 'OrderControlOrderAmount',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['orderAmount'], propertyType: 'number' },
        },
        {
          key: 'OrderControlDeliveryDate',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['deliveryDate'], propertyType: 'string' },
        },
        {
          key: 'OrderControlWarehouseNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['warehouseNumber'], propertyType: 'string' },
        },
        {
          key: 'OrderControlWarehouse',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['warehouse'], propertyType: 'string' },
        },
        {
          key: 'OrderControlSupplierNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['supplierNumber'], propertyType: 'string' },
        },
        {
          key: 'OrderControlSupplierName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['supplierName'], propertyType: 'string' },
        },
        {
          key: 'OrderControlManufacturer',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['manufacturer'], propertyType: 'string' },
        },
        {
          key: 'OrderControlBusinessNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['businessNumber'], propertyType: 'string' },
        },
        {
          key: 'OrderControlBusinessName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['businessName'], propertyType: 'string' },
        },
        {
          key: 'OrderControlPackage',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['package'], propertyType: 'number' },
        },
        {
          key: 'OrderControlUnit',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['unit'], propertyType: 'string' },
        },
        // NOTE: 以下は展開機能を実装しないと使えないので、一旦コメントアウト
        // {
        //   key: 'OrderControlCustomerName',
        //   isVariableWidth: true,
        //   isNarrowable: true,
        //   isEditable: false,
        //   sortOption: {
        //     isSortable: true,
        //     isAsc: true,
        //   },
        //   propertyPath: { paths: ['customerName'], propertyType: 'string' },
        // },
        // {
        //   key: 'OrderControlSalesDestinationName',
        //   isVariableWidth: true,
        //   isNarrowable: true,
        //   isEditable: false,
        //   sortOption: {
        //     isSortable: true,
        //     isAsc: true,
        //   },
        //   propertyPath: { paths: ['salesDestinationName'], propertyType: 'string' },
        // },
        {
          key: 'OrderControlAggregateBusinessResponsibleUsers',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['aggregateBusinessResponsibleUsers'], propertyType: 'string' },
        },
        {
          key: 'OrderControlLeadTime',
          isVariableWidth: true,
          isNarrowable: false,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['leadTime'], propertyType: 'string' },
        },
        {
          key: 'OrderControlStdInventoryIndex',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['stdInventoryIndex'], propertyType: 'string' },
        },
        HEADER_MODIFY_TABLE_CONTENT_PROPERTY,
      ],
      options: {
        stableColumnCount: 3,
        resizableHeaderColumn: true,
      },
    };
  };

  return {
    state,
    $reset,
    getEnabledOrderStatus,
    onNextUpdateBusiness,
    onChangePaging,
    sortTableContentItems,
    filterTableContentItems,
    fetchTableContentProperties,
    resetPagingOptions,
    fetchItems,
    onAppliedFilterModal,
    changeOrderStatusFilterItems,
    containsRecommendOrder,
    getFilterItemsValue,
    putHiddenRecommended,
  };
});

/* TODO: NetWorkResultと区別したい（こっちはUI用） */

/**
 *　ロード中
 */
type Loading = {
  type: 'loading';
};

/**
 *　成功
 */
type Success = {
  type: 'success';
};

/**
 *　失敗
 */
type Error = {
  type: 'error';
  errorMessage: string;
};

/**
 *　ゼロマッチ
 */
type ZeroMatch = {
  type: 'zeroMatch';
};

/**
 * API等の結果をラップするインターフェース
 */
export interface LoadingState {
  status: Loading | Success | Error | ZeroMatch;
}

/**
 * API等の結果をラップするインターフェース
 */
export interface LoadingState {
  status: Loading | Success | Error | ZeroMatch;
}

/**
 * 発注管理ストアの状態を定義するインターフェース。
 */
interface IOrderControlState {
  items: IOrderControl[];
  onUpdatedBusiness: [() => Promise<void>];
  pagingOptions?: IPagingOptions;
  loadingState: LoadingState;
  tableContentProperties: TableContentHeader;
  tableContentItems: TableContentItem[];
  sortKey?: string;
  isAsc?: boolean;
  filterState: NarrowState[];
  checkedIds: string[];
  orderStatusFilterItems: OrderStatusFilterItemForOrderBusiness;
  updateOrderLoadingState: LoadingState;
  aggregateBusinessIdsHasRecommendOrder: string[]; // 推奨情報を持っている集計ID
  summaryCount: {
    Recommended: number | undefined;
    ApprovalRequested: number | undefined;
    ApprovedAwaitingDeliveryDate: number | undefined;
  };
  isHiddenRecommendShow: boolean;
}

const HeaderKeyToColumn: Record<string, string> = {
  OrderControlOrderDate: 'createdOrderDate',
  OrderControlOrderAmount: 'orderAmount',
  OrderControlDeliveryDate: 'desiredDeliveryDate',
  OrderControlWarehouseNumber: 'warehouseNumber',
  OrderControlWarehouse: 'warehouse',
  OrderControlSupplierNumber: 'supplierNumber',
  OrderControlSupplierName: 'supplierName',
  OrderControlManufacturer: 'manufacturer',
  OrderControlBusinessNumber: 'businessNumber',
  OrderControlBusinessName: 'businessName',
  OrderControlPackage: 'package',
  OrderControlUnit: 'unit',
  OrderControlAggregateBusinessResponsibleUsers: 'aggregateBusinessResponsibleUser',
  OrderControlLeadTime: 'leadTime',
  OrderControlStdInventoryIndex: 'stdInventoryIndex',
};
